import type { Content, SpellDef } from '../data/schema';
import { recomputeStats } from './equipment';
import { availableSpells } from './meta/unlocks';
import { ZERO_MODS, type OwnedSpell, type Player } from './types';
import type { World } from './world';

export const MAX_SPELL_SLOTS = 4;
export const SPELL_MAX_LEVEL = 5;
export type UpgradeKind = 'damage' | 'cooldown' | 'count' | 'radius';
export type LevelUpCard =
  | { kind: 'spell'; id: string; name: string; description: string; icon: string; spellId: string }
  | { kind: 'upgrade'; id: string; name: string; description: string; icon: string; spellId: string; upgrade: UpgradeKind }
  | { kind: 'passive'; id: string; name: string; description: string; icon: null; passiveId: string };

export function xpToNext(level: number): number { const n = level - 1; return 30 + 15 * n + 5 * n * n; }
export function effectiveCooldown(spell: SpellDef, owned: OwnedSpell, bonusPct = 0): number { return Math.max(0.15, spell.cooldown * (1 - (owned.mods.cooldownPct + bonusPct) / 100)); }

export function grantXp(world: World, p: Player, amount: number): number {
  p.xp += Math.floor(amount * (1 + world.meta.xpPct / 100) * (1 + p.stats.sagesse / 100));
  let gained = 0;
  while (p.xp >= p.xpNext) { p.xp -= p.xpNext; p.level += 1; p.xpNext = xpToNext(p.level); gained++; world.events.push({ type: 'levelUp', level: p.level }); }
  return gained;
}

const UPGRADE_TEXT: Record<UpgradeKind, [string, string]> = { damage: ['Puissance', '+25 % de dégâts'], cooldown: ['Hâte', '-15 % de temps de recharge'], count: ['Multiplication', '+1 projectile'], radius: ['Amplitude', '+30 % de rayon'] };

function ownedOf(p: Player, spellId: string): OwnedSpell | undefined { return p.primary.id === spellId ? p.primary : p.spells.find((s) => s.id === spellId); }

/**
 * Une carte Puissance n'a d'effet que sur un sort qui inflige des dégâts (projectile, zone, mêlée, ronces, attirance
 * avec dégâts, buff de dégâts, invocation explosive). Les sorts de pur déplacement ou de bouclier la gardent
 * (comportement historique, qui fixe le tirage du Xélor).
 */
function offersDamage(c: Content, s: SpellDef): boolean {
  const deals = (e: SpellDef['effects'][number]): boolean => {
    switch (e.type) {
      case 'projectile': case 'zone': case 'melee': case 'thorns': return true;
      case 'pull': return e.damage !== undefined;
      case 'buff': return e.damagePct > 0;
      case 'summon': return c.summons[e.summonId]?.explode !== undefined;
      default: return false;
    }
  };
  return s.effects.some(deals) || s.effects.every((e) => e.type === 'blink' || e.type === 'shield');
}

function buildPool(world: World, p: Player): { spells: LevelUpCard[]; rest: LevelUpCard[] } {
  const c = world.content;
  const spells: LevelUpCard[] = [];
  if (p.spells.length < MAX_SPELL_SLOTS) for (const id of availableSpells(c, p.defId, world.meta)) { const s = c.spells[id]; if (s && !ownedOf(p, id)) spells.push({ kind: 'spell', id: `spell:${id}`, name: s.name, description: s.description, icon: s.icon, spellId: id }); }
  const rest: LevelUpCard[] = [];
  for (const o of [p.primary, ...p.spells]) {
    const s = c.spells[o.id];
    if (!s || o.level >= SPELL_MAX_LEVEL) continue;
    const kinds: UpgradeKind[] = [];
    if (offersDamage(c, s)) kinds.push('damage');
    if (effectiveCooldown(s, o, world.meta.cooldownPct) > 0.15) kinds.push('cooldown');
    if (s.effects.some((e) => e.type === 'projectile')) kinds.push('count');
    if (s.effects.some((e) => e.type === 'zone' || e.type === 'melee' || e.type === 'pull' || e.type === 'thorns')) kinds.push('radius');
    for (const k of kinds) rest.push({ kind: 'upgrade', id: `upgrade:${o.id}:${k}`, name: `${s.name} : ${UPGRADE_TEXT[k][0]}`, description: UPGRADE_TEXT[k][1], icon: s.icon, spellId: o.id, upgrade: k });
  }
  for (const pa of Object.values(c.passives)) {
    if (pa.classes && !pa.classes.includes(p.defId)) continue;
    const stacks = p.passives[pa.id] ?? 0;
    if (!pa.stackable && stacks > 0) continue;
    if (pa.maxStacks !== undefined && stacks >= pa.maxStacks) continue;
    if (pa.requireHpBelow !== undefined && p.hp >= pa.requireHpBelow * p.hpMax) continue;
    rest.push({ kind: 'passive', id: `passive:${pa.id}`, name: pa.name, description: pa.description, icon: null, passiveId: pa.id });
  }
  return { spells, rest };
}

export function drawCards(world: World, p: Player): LevelUpCard[] {
  const { spells, rest } = buildPool(world, p);
  const picks: LevelUpCard[] = [];
  if (spells.length) picks.push(spells[world.rng.int(0, spells.length - 1)]!);
  const pool = [...spells.filter((s) => !picks.includes(s)), ...rest];
  while (picks.length < 3 && pool.length) picks.push(pool.splice(world.rng.int(0, pool.length - 1), 1)[0]!);
  return picks;
}

export function applyCard(world: World, p: Player, card: LevelUpCard): void {
  const c = world.content;
  if (card.kind === 'spell') {
    if (p.spells.length >= MAX_SPELL_SLOTS || ownedOf(p, card.spellId) || !c.spells[card.spellId]) return;
    p.spells.push({ id: card.spellId, level: 1, mods: { ...ZERO_MODS } });
    p.cooldowns[card.spellId] = 0;
  } else if (card.kind === 'upgrade') {
    const o = ownedOf(p, card.spellId);
    if (!o || o.level >= SPELL_MAX_LEVEL) return;
    o.level += 1;
    if (card.upgrade === 'damage') o.mods.damagePct += 25;
    else if (card.upgrade === 'cooldown') o.mods.cooldownPct += 15;
    else if (card.upgrade === 'count') o.mods.countBonus += 1;
    else o.mods.radiusPct += 30;
  } else {
    const pa = c.passives[card.passiveId];
    if (!pa) return;
    p.passives[card.passiveId] = (p.passives[card.passiveId] ?? 0) + 1;
    for (const k of Object.keys(pa.stats) as (keyof typeof pa.stats)[]) p.passiveStats[k] += pa.stats[k] ?? 0;
    p.speedPct += pa.speedPct;
    p.lifestealPct += pa.lifestealPct;
    p.summonPct += pa.summonPct;
    recomputeStats(world, p);
    const heal = Math.floor(p.hpMax * pa.healPct) + pa.healFlat;
    if (heal > 0) { const before = p.hp; p.hp = Math.min(p.hpMax, p.hp + heal); world.events.push({ type: 'heal', amount: p.hp - before, source: 'passive' }); }
  }
  world.events.push({ type: 'cardChosen', cardId: card.id });
}
